import { Close } from "@mui/icons-material";
import {
    Box,
    CircularProgress,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Snackbar,
    Tooltip,
    Typography,
} from "@mui/material";
import { type ExplorerBookmarkState } from "@novorender/data-js-api";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { WidgetMenuButtonWrapper } from "components/widgetMenuButtonWrapper";
import { featuresConfig } from "config/features";
import { BookmarkCancelledError } from "features/bookmarks/useCreateBookmark";
import { selectViewMode } from "features/render";
import { useAbortController } from "hooks/useAbortController";
import { explorerActions, selectIsOnline } from "slices/explorer";
import { telemetry } from "telemetry";
import { ViewMode } from "types/misc";

import { ShareLinkClipboardError, useShareLink } from "./useShareLink";

enum Status {
    Initial,
    Loading,
    Success,
}

export function ShareLink({
    variant = "default",
    nameKey = featuresConfig.shareLink.nameKey,
    explorerStateOverwrite = { forms: undefined },
    onClick,
}: {
    variant?: "default" | "primaryMenu" | "menuItem";
    nameKey?: string;
    explorerStateOverwrite?: Partial<ExplorerBookmarkState>;
    onClick?: () => void;
}) {
    const { t } = useTranslation();
    const { Icon, offline } = featuresConfig.shareLink;

    const viewMode = useAppSelector(selectViewMode);
    const isOnline = useAppSelector(selectIsOnline);
    const dispatch = useAppDispatch();
    const shareLink = useShareLink();

    const [status, setStatus] = useState(Status.Initial);
    const [abortController] = useAbortController();

    const loading = status === Status.Loading;
    const disabled = (!isOnline && !offline) || viewMode === ViewMode.Panorama;

    const createLink = async () => {
        if (status !== Status.Initial || disabled) {
            return;
        }

        const abortSignal = abortController.current.signal;
        setStatus(Status.Loading);

        try {
            await shareLink(explorerStateOverwrite, abortSignal);

            if (variant === "primaryMenu" || variant === "menuItem") {
                dispatch(explorerActions.setSnackbarMessage({ msg: t("copiedToClipboard") }));
                setTimeout(() => setStatus(Status.Initial), 1000);
            } else {
                setStatus(Status.Success);
            }

            onClick?.();
        } catch (e) {
            if (abortSignal.aborted || e instanceof BookmarkCancelledError) {
                setStatus(Status.Initial);
                return;
            } else if (variant === "primaryMenu" || variant === "menuItem") {
                if (e instanceof ShareLinkClipboardError) {
                    dispatch(
                        explorerActions.setSnackbarMessage({
                            msg: t("shareLinkClipboardError"),
                            link: e.link,
                        }),
                    );
                } else {
                    dispatch(explorerActions.setSnackbarMessage({ msg: t("shareLinkSaveError") }));
                }
            }

            telemetry.trackCaughtException({ source: "shareLink.shareLink.createLink", category: "data", error: e });
            setStatus(Status.Initial);
        }
    };

    if (variant === "primaryMenu") {
        return (
            <>
                <Tooltip title={t(nameKey)} placement="top">
                    <Box>
                        <IconButton onClick={createLink} disabled={disabled || loading}>
                            <Box position="relative" display="inline-flex" justifyContent="center" alignItems="center">
                                {loading ? (
                                    <CircularProgress thickness={2.5} sx={{ position: "absolute" }} size={36} />
                                ) : null}
                                <Icon />
                            </Box>
                        </IconButton>
                    </Box>
                </Tooltip>
            </>
        );
    }

    if (variant === "menuItem") {
        return (
            <>
                <MenuItem disabled={disabled || loading} onClick={createLink}>
                    <ListItemIcon>
                        <Box position="relative" display="inline-flex" justifyContent="center" alignItems="center">
                            {loading ? (
                                <CircularProgress thickness={2.5} sx={{ position: "absolute" }} size={24} />
                            ) : null}
                            <Icon />
                        </Box>
                    </ListItemIcon>
                    <ListItemText>{t(nameKey)}</ListItemText>
                </MenuItem>
            </>
        );
    }

    return (
        <>
            <Snackbar
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                sx={{ width: { xs: "auto", sm: 350 }, bottom: { xs: "auto", sm: 24 }, top: { xs: 24, sm: "auto" } }}
                autoHideDuration={2500}
                open={status === Status.Success}
                onClose={() => setStatus(Status.Initial)}
                message={t("copiedToClipboard")}
                action={
                    <IconButton
                        size="small"
                        aria-label="close"
                        color="inherit"
                        onClick={() => setStatus(Status.Initial)}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                }
            />
            <WidgetMenuButtonWrapper
                activeCurrent={status !== Status.Initial}
                activeElsewhere={disabled}
                onClick={createLink}
            >
                <IconButton disabled={disabled || loading} size="large">
                    <Box position="relative" display="inline-flex" justifyContent="center" alignItems="center">
                        {loading ? <CircularProgress thickness={2.5} sx={{ position: "absolute" }} size={36} /> : null}
                        <Icon />
                    </Box>
                </IconButton>
                <Typography>{t(nameKey)}</Typography>
            </WidgetMenuButtonWrapper>
        </>
    );
}
